import { translateAuthError, useAuth } from "@/auth/AuthProvider";
import { BrewisMark } from "@/components/BrewisMark";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Alert, Box, Button, Link as MuiLink, Stack, TextField, Typography } from "@mui/material";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { FirebaseError } from "firebase/app";
import { useState } from "react";

const MARKETING_URL = "https://brewis.io";

export const Route = createFileRoute("/login")({
  beforeLoad: ({ context }) => {
    if (context.auth.status === "authenticated") {
      throw redirect({ to: "/leads" });
    }
  },
  component: LoginPage,
});

function LoginPage() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signIn(email.trim(), password);
      await navigate({ to: "/leads" });
    } catch (err) {
      const code = err instanceof FirebaseError ? err.code : undefined;
      setError(translateAuthError(code));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        bgcolor: "background.default",
      }}
    >
      {/* Ліва брендова панель */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          justifyContent: "space-between",
          p: 6,
          bgcolor: "primary.main",
          color: "primary.contrastText",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            opacity: 0.1,
            backgroundImage: "radial-gradient(circle, currentColor 1px, transparent 1px)",
            backgroundSize: "24px 24px",
            pointerEvents: "none",
          }}
        />
        <Stack spacing={3} sx={{ position: "relative", maxWidth: 420 }}>
          <MuiLink
            href={MARKETING_URL}
            target="_blank"
            rel="noopener noreferrer"
            underline="none"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1.5,
              color: "inherit",
              transition: "opacity 150ms",
              "&:hover": { opacity: 0.85 },
            }}
          >
            <BrewisMark size={44} variant="onPrimary" />
            <Typography variant="h2" sx={{ color: "inherit", fontWeight: 600 }}>
              Brewis
            </Typography>
          </MuiLink>
          <Typography variant="h1" sx={{ color: "inherit" }}>
            Backoffice
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "inherit", opacity: 0.85, lineHeight: 1.6, fontSize: "1rem" }}
          >
            Керуй клієнтами, моніторь ризики і закривай угоди з одного місця.
          </Typography>
        </Stack>
        <Typography variant="body2" sx={{ color: "inherit", opacity: 0.7, position: "relative" }}>
          admin.brewis.io
        </Typography>
      </Box>

      {/* Права панель — форма */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          position: "relative",
          px: {
            xs: "max(16px, calc(var(--safe-left) + 12px))",
            sm: "max(24px, calc(var(--safe-left) + 16px))",
          },
          pr: {
            xs: "max(16px, calc(var(--safe-right) + 12px))",
            sm: "max(24px, calc(var(--safe-right) + 16px))",
          },
          py: {
            xs: "max(24px, calc(var(--safe-top) + 12px))",
            md: 8,
          },
          pb: {
            xs: "max(24px, calc(var(--safe-bottom) + 16px))",
            md: 8,
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "max(12px, calc(var(--safe-top) + 8px))",
            right: "max(12px, calc(var(--safe-right) + 8px))",
          }}
        >
          <ThemeToggle />
        </Box>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            maxWidth: 400,
            mx: "auto",
            width: "100%",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ mb: 5, display: { xs: "flex", md: "none" } }}
          >
            <BrewisMark size={36} />
            <Typography variant="h3">Brewis Backoffice</Typography>
          </Stack>

          <Typography variant="h2" sx={{ mb: 1 }}>
            Увійти
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Внутрішня консоль Brewis
          </Typography>

          <Stack component="form" spacing={2.5} onSubmit={handleSubmit}>
            <TextField
              label="Email"
              type="email"
              autoComplete="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              label="Пароль"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {error && <Alert severity="error">{error}</Alert>}
            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
              size="large"
              sx={{ mt: 1 }}
            >
              {submitting ? "Входжу…" : "Увійти"}
            </Button>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}
